import {
  startOfWeek,
  endOfWeek,
  format,
  addWeeks,
  addDays,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  Locale,
} from 'date-fns'
import * as locales from 'date-fns/locale'
import i18n from 'i18next'

import { WeekRange, WeekDay } from '../types'

const getLocale = () =>
  (locales as { [key: string]: Locale })[i18n.language] || locales.enUS

const formatDate = (date: Date): string => format(date, 'yyyy-MM-dd')

const DAY_NAMES = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const
const EXPECTED_HOURS_PER_DAY = [8, 8, 8, 8, 8, 8, 8] as const

export const getCurrentWeekRange = (): WeekRange => {
  const today = new Date()
  const monday = startOfWeek(today, { weekStartsOn: 1 })
  const sunday = endOfWeek(today, { weekStartsOn: 1 })

  return {
    startDate: formatDate(monday),
    endDate: formatDate(sunday),
  }
}

export const getWeekDates = (weekRange: WeekRange): WeekDay[] => {
  const monday = new Date(weekRange.startDate)
  const locale = getLocale()

  return DAY_NAMES.map((name, i) => {
    const date = addDays(monday, i)
    const formattedLabel = format(date, 'EEE, MMM d', { locale })

    return {
      label: formattedLabel,
      key: name.toLowerCase(),
      date: formatDate(date),
      total: `/${EXPECTED_HOURS_PER_DAY[i]}h`,
    }
  })
}

export const navigateWeek = (
  weekRange: WeekRange,
  direction: 'prev' | 'next',
): WeekRange => {
  const currentStart = new Date(weekRange.startDate)
  const weeksToAdd = direction === 'prev' ? -1 : 1
  const newStart = addWeeks(currentStart, weeksToAdd)
  const newEnd = endOfWeek(newStart, { weekStartsOn: 1 })

  return {
    startDate: formatDate(newStart),
    endDate: formatDate(newEnd),
  }
}

export const getMonthRange = (date: Date): WeekRange => {
  const firstDay = startOfMonth(date)
  const lastDay = endOfMonth(date)

  return {
    startDate: formatDate(firstDay),
    endDate: formatDate(lastDay),
  }
}

export const getDaysInMonth = (monthRange: WeekRange): Date[] => {
  return eachDayOfInterval({
    start: new Date(monthRange.startDate),
    end: new Date(monthRange.endDate),
  })
}

export const formatDateKey = (date: Date): string => format(date, 'yyyy-MM-dd')

export const weekRangeToApiFilters = (weekRange: WeekRange) => {
  const localStart = new Date(`${weekRange.startDate}T00:00:00`)
  const localEnd = addDays(new Date(`${weekRange.endDate}T00:00:00`), 1)
  return {
    'recordedAt.gte': localStart.toISOString(),
    'recordedAt.lt': localEnd.toISOString(),
  }
}

export const formatDateWithLocale = (
  date: Date,
  dateFormat: string,
): string => {
  const locale = getLocale()
  return format(date, dateFormat, { locale })
}
